import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const courseTypes = [
  { id: 'welcome', label: 'Welcome snacks' },
  { id: 'starter', label: 'Forrett' },
  { id: 'main', label: 'Hovedrett' },
  { id: 'dessert', label: 'Dessert' },
  { id: 'nightfood', label: 'Nattmat' },
  { id: 'coffee', label: 'Kaffe & Te' },
];

const cateringTypes = [
  { value: 'sitdown', label: 'Sit-down middag' },
  { value: 'buffet', label: 'Buffet' },
  { value: 'family', label: 'Family-style' },
  { value: 'grazing', label: 'Grazing / Tapas' },
  { value: 'foodtruck', label: 'Food Trucks' },
];

const defaultDish = { name: '', pricePerPerson: '', vendor: '', allergens: '' };

const Inp = ({ label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="text-[10px] font-sans font-semibold text-stone-500 uppercase tracking-wide mb-0.5 block">{label}</label>
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);

export const FoodPlanner = ({ food, onChange, hasChildren, hasAlcohol, guestAllrgyes }) => {
  const [openCourse, setOpenCourse] = useState(null);
  const update = (field, value) => onChange({ ...food, [field]: value });
  const updateCourse = (courseId, dishes) => onChange({ ...food, courses: { ...(food.courses || {}), [courseId]: dishes } });

  const updateDish = (courseId, idx, field, value) => {
    const dishes = [...(food.courses?.[courseId] || [])];
    dishes[idx] = { ...dishes[idx], [field]: value };
    updateCourse(courseId, dishes);
  };

  return (
    <div data-testid="food-planner" className="space-y-4">
      <div>
        <h3 className="font-sans font-semibold text-stone-800 text-sm">Mat & Drikke</h3>
        <p className="text-xs font-sans text-stone-500 mt-0.5">Plan the menu for the wedding</p>
      </div>

      {/* Catering type */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4">
        <label className="text-xs font-sans font-medium text-stone-600 mb-2 block">Type catering</label>
        <div className="flex flex-wrap gap-2">
          {cateringTypes.map(t => (
            <button key={t.value} type="button" onClick={() => update('cateringType', t.value)}
              className={`text-xs font-sans px-3 py-2 rounded-xl border transition-all ${
                food.cateringType === t.value ? 'bg-primary/10 border-primary text-primary font-semibold' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-400'
              }`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Courses */}
      <div className="space-y-2">
        {courseTypes.map(course => {
          const dishes = food.courses?.[course.id] || [];
          const isOpen = openCourse === course.id;
          return (
            <div key={course.id} className={`border rounded-xl transition-all ${isOpen ? 'border-primary/30 bg-white' : 'border-stone-200'}`}>
              <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setOpenCourse(isOpen ? null : course.id)}>
                <div>
                  <p className="text-sm font-sans font-medium text-stone-700">{course.label}</p>
                  <p className="text-xs font-sans text-stone-400">{dishes.length} retter</p>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
              </div>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2 border-t border-stone-100 pt-2">
                  {dishes.map((dish, idx) => (
                    <div key={idx} className="bg-stone-50/80 rounded-lg p-2.5 space-y-2">
                      <div className="flex items-center gap-2">
                        <input value={dish.name} onChange={e => updateDish(course.id, idx, 'name', e.target.value)} placeholder="Rettnavn"
                          className="flex-1 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                        <button onClick={() => updateCourse(course.id, dishes.filter((_, i) => i !== idx))} className="text-stone-400 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        <div>
                          <label className="text-[9px] font-sans text-stone-400 uppercase">Price/person</label>
                          <input type="number" value={dish.pricePerPerson || ''} onChange={e => updateDish(course.id, idx, 'pricePerPerson', e.target.value)} placeholder="0"
                            className="w-full text-[11px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                        </div>
                        <div>
                          <label className="text-[9px] font-sans text-stone-400 uppercase">Vendor</label>
                          <input value={dish.vendor || ''} onChange={e => updateDish(course.id, idx, 'vendor', e.target.value)} placeholder="Vendor"
                            className="w-full text-[11px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                        </div>
                        <div>
                          <label className="text-[9px] font-sans text-stone-400 uppercase">Allrgener</label>
                          <input value={dish.allergens || ''} onChange={e => updateDish(course.id, idx, 'allergens', e.target.value)} placeholder="e.g. nuts"
                            className="w-full text-[11px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => updateCourse(course.id, [...dishes, { ...defaultDish }])}
                    className="flex items-center gap-1 text-xs font-sans text-stone-500 hover:text-primary">
                    <Plus className="w-3 h-3" /> Add dish
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Wedding Cake */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <h4 className="text-xs font-sans font-semibold text-stone-500 uppercase tracking-wide">Wedding cake</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Inp label="Type/smak" value={food.cakeType} onChange={v => update('cakeType', v)} placeholder="e.g. sjokolade" />
          <Inp label="Size/layers" value={food.cakeSize} onChange={v => update('cakeSize', v)} placeholder="e.g. 3 layers" />
          <Inp label="Vendor" value={food.cakeVendor} onChange={v => update('cakeVendor', v)} placeholder="Baker" />
          <Inp label="Price (kr)" value={food.cakePrice} onChange={v => update('cakePrice', v)} type="number" />
        </div>
      </div>

      {/* Children's menu */}
      {hasChildren && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200/50 p-4 space-y-2">
          <h4 className="text-xs font-sans font-semibold text-amber-700 uppercase tracking-wide">Childemeny</h4>
          <p className="text-xs font-sans text-amber-600/80">There are children among the guests. Plan an adapted kids menu.</p>
          <textarea value={food.childrenMenu || ''} onChange={e => update('childrenMenu', e.target.value)}
            placeholder="e.g. Chicken nuggets, pasta, frukt, juice" rows={2}
            className="w-full text-sm font-sans px-3 py-2 border border-amber-200 rounded-xl bg-white focus:border-primary focus:outline-none" />
        </div>
      )}

      {/* Vendor meals */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4">
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Vendor meal count" value={food.vendorMeals} onChange={v => update('vendorMeals', v)} type="number" placeholder="0" />
          <Inp label="Vendor meal type" value={food.vendorMealType} onChange={v => update('vendorMealType', v)} placeholder="e.g. simple buffet" />
        </div>
      </div>

      {/* Detailed Drink Planner */}
      {hasAlcohol !== 'no' && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-4">
          <h4 className="text-xs font-sans font-semibold text-stone-500 uppercase tracking-wide">Detailed drink planner</h4>
          <div className="space-y-2">
            {[
              { id: 'champagne', label: 'Champagne / Sparkling', unit: 'bottles' },
              { id: 'wine_white', label: 'Whitevin', unit: 'bottles' },
              { id: 'wine_red', label: 'Red wine', unit: 'bottles' },
              { id: 'wine_rose', label: 'Rosévin', unit: 'bottles' },
              { id: 'beer', label: 'Beer / Cider', unit: 'units' },
              { id: 'spirits', label: 'Sprit', unit: 'bottles' },
              { id: 'cocktail_mix', label: 'Cocktail-miksere', unit: 'liter' },
              { id: 'mocktails', label: 'Mocktails / Non-alcoholic', unit: 'liter' },
              { id: 'water', label: 'Vann', unit: 'bottles' },
              { id: 'soft_drinks', label: 'Mineralvann / Brus', unit: 'bottles' },
              { id: 'coffee_tea', label: 'Kaffe & Te', unit: 'porsjoner' },
              { id: 'ice', label: 'Is', unit: 'kg' },
            ].map(drink => {
              const d = (food.drinks || {})[drink.id] || {};
              const updateDrink = (field, val) => {
                const drinks = { ...(food.drinks || {}), [drink.id]: { ...d, [field]: val } };
                update('drinks', drinks);
              };
              return (
                <div key={drink.id} className="grid grid-cols-5 gap-1.5 items-center">
                  <p className="text-[10px] font-sans text-stone-600 col-span-1 truncate">{drink.label}</p>
                  <div>
                    <input type="number" value={d.quantity || ''} onChange={e => updateDrink('quantity', e.target.value)} placeholder="Ant."
                      className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                  </div>
                  <div>
                    <input type="number" value={d.pricePerUnit || ''} onChange={e => updateDrink('pricePerUnit', e.target.value)} placeholder="Kr/enhet"
                      className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                  </div>
                  <div className="text-[10px] font-sans text-stone-400 text-right">
                    {d.quantity && d.pricePerUnit ? `${(parseFloat(d.quantity) * parseFloat(d.pricePerUnit)).toLocaleString('en-US')} kr` : '—'}
                  </div>
                  <div>
                    <input value={d.vendor || ''} onChange={e => updateDrink('vendor', e.target.value)} placeholder="Vendor"
                      className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between border-t border-stone-100 pt-2">
            <p className="text-[10px] font-sans font-bold text-stone-500 uppercase">Total drikkekostnad</p>
            <p className="text-xs font-sans font-bold text-primary">
              {Object.values(food.drinks || {}).reduce((s, d) => s + ((parseFloat(d.quantity) || 0) * (parseFloat(d.pricePerUnit) || 0)), 0).toLocaleString('en-US')} kr
            </p>
          </div>
        </div>
      )}

      {/* Signature Cocktails */}
      {hasAlcohol !== 'no' && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
          <h4 className="text-xs font-sans font-semibold text-stone-500 uppercase tracking-wide">Signaturcocktails / Mocktails</h4>
          {(food.signatureDrinks || []).map((drink, idx) => (
            <div key={idx} className="bg-stone-50/80 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input value={drink.name || ''} onChange={e => {
                  const list = [...(food.signatureDrinks || [])];
                  list[idx] = { ...list[idx], name: e.target.value };
                  update('signatureDrinks', list);
                }} placeholder="Drinknavn"
                  className="flex-1 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                <button type="button" onClick={() => update('signatureDrinks', (food.signatureDrinks || []).filter((_, i) => i !== idx))} className="text-stone-400 hover:text-red-500">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <div>
                  <label className="text-[9px] font-sans text-stone-400 uppercase">Ingredienser</label>
                  <input value={drink.ingredients || ''} onChange={e => {
                    const list = [...(food.signatureDrinks || [])];
                    list[idx] = { ...list[idx], ingredients: e.target.value };
                    update('signatureDrinks', list);
                  }} placeholder="e.g. gin, lime, agurk"
                    className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[9px] font-sans text-stone-400 uppercase">Mengde</label>
                  <input value={drink.servings || ''} onChange={e => {
                    const list = [...(food.signatureDrinks || [])];
                    list[idx] = { ...list[idx], servings: e.target.value };
                    update('signatureDrinks', list);
                  }} placeholder="Ant. porsjoner"
                    className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                </div>
                <div>
                  <label className="text-[9px] font-sans text-stone-400 uppercase">Total price</label>
                  <input type="number" value={drink.price || ''} onChange={e => {
                    const list = [...(food.signatureDrinks || [])];
                    list[idx] = { ...list[idx], price: e.target.value };
                    update('signatureDrinks', list);
                  }} placeholder="0"
                    className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                </div>
              </div>
              <textarea value={drink.recipe || ''} onChange={e => {
                const list = [...(food.signatureDrinks || [])];
                list[idx] = { ...list[idx], recipe: e.target.value };
                update('signatureDrinks', list);
              }} placeholder="Oppskrift / instruksjoner" rows={2}
                className="w-full text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
            </div>
          ))}
          <button type="button" onClick={() => update('signatureDrinks', [...(food.signatureDrinks || []), { name: '', ingredients: '', recipe: '', servings: '', price: '' }])}
            className="flex items-center gap-1 text-xs font-sans text-stone-500 hover:text-primary">
            <Plus className="w-3 h-3" /> Add signature drink
          </button>
        </div>
      )}

      {/* Bartender + Cooling */}
      {hasAlcohol !== 'no' && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4">
          <div className="grid grid-cols-2 gap-3">
            <Inp label="Bartender" value={food.bartender} onChange={v => update('bartender', v)} placeholder="Name / company" />
            <Inp label="Ice / cooling / glasses" value={food.coolingNotes} onChange={v => update('coolingNotes', v)} placeholder="e.g. wine cooler, ice machine" />
          </div>
        </div>
      )}

      {/* Heating notes for buffet */}
      {food.cateringType === 'buffet' && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4">
          <Inp label="Heating / serving plan" value={food.heatingNotes} onChange={v => update('heatingNotes', v)} placeholder="Plan to keep food warm" />
        </div>
      )}

      {/* Guest dietary summary */}
      {guestAllrgyes?.length > 0 && (
        <div className="bg-blue-50 rounded-2xl border border-blue-200/50 p-4">
          <h4 className="text-xs font-sans font-semibold text-blue-700 uppercase tracking-wide mb-2">Diet summary from guest list</h4>
          <div className="flex flex-wrap gap-2">
            {guestAllrgyes.map((item, i) => (
              <span key={i} className="text-xs font-sans bg-white border border-blue-200 text-blue-700 px-2.5 py-1 rounded-full">{item}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
